import type { Product, ProductFormSchema } from "schemas/generated/product";

import { useCallback } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { productRouter, productUtils } from "@editor/trpc/product";

import { successToast } from "@replo/design-system/components/alert/Toast";
import { ReploError } from "schemas/errors";

class ProductUpdateError extends ReploError {}

interface ProductUpdateResponse {
  updateProduct: ({
    product,
    formData,
    creatingNewProduct,
  }: {
    product: Product;
    formData: ProductFormSchema;
    creatingNewProduct: boolean;
  }) => Promise<void>;
  isLoading: boolean;
}

export const useProductUpdate = (): ProductUpdateResponse => {
  const projectId = useCurrentProjectId();

  const updateProductMutation =
    productRouter.createOrUpdateReploProduct.useMutation({
      onSuccess: async () => {
        await productUtils.get.invalidate();
        await productUtils.find.invalidate();
        await productUtils.list.invalidate();
      },
    });

  const updateProduct = useCallback(
    async ({
      product,
      formData,
      creatingNewProduct,
    }: {
      product: Product;
      formData: ProductFormSchema;
      creatingNewProduct: boolean;
    }) => {
      if (!projectId) {
        throw new ProductUpdateError({
          message: "Missing required project ID for product update",
          additionalData: {
            projectId,
          },
        });
      }

      // Convert ProductMediaItem to simplified format - just assetId and sortOrder
      const mediaItems = (formData.media ?? []).map((item) => ({
        assetId: item.assetId,
        sortOrder: item.sortOrder,
      }));

      const updateData = {
        id: product.id,
        title: formData.name,
        description: formData.description,
        type: formData.productType,
        externalCheckoutUrl: formData.externalCheckoutUrl ?? null,
        price: formData.price ?? 0,
        compareAtPrice: formData.compareAtPrice,
        media: mediaItems,
        productFiles: formData.productFiles ?? [],
        productEmail: formData.hasFulfillmentEmail
          ? formData.productEmail
          : null,
      };

      try {
        await updateProductMutation.mutateAsync(
          {
            projectId,
            productData: updateData,
          },
          {
            onSuccess: () => {
              successToast(
                creatingNewProduct ? "Product Created" : "Product Updated",
                `Your product has been ${
                  creatingNewProduct ? "created" : "updated"
                } successfully.`,
              );
            },
          },
        );
      } catch (error) {
        throw new ProductUpdateError({
          message: "Failed to update product",
          additionalData: {
            productId: product.id,
            error: error instanceof Error ? error.message : "Unknown error",
          },
        });
      }
    },
    [projectId, updateProductMutation],
  );

  return {
    updateProduct,
    isLoading: updateProductMutation.isPending,
  };
};
